import {doc, getDoc, onSnapshot, type Unsubscribe} from 'firebase/firestore';
import {useEffect, useState} from 'react';
import {env} from '../../constants';
import {firebaseAuth, firebaseFirestore} from '../../setup/firebase';
import type {GenerationJobProject} from './schema';

function docToProject(id: string, data: Record<string, unknown> | undefined): GenerationJobProject | undefined {
  if (!data) return undefined;
  return {
    id,
    name: typeof data.name === 'string' ? data.name : undefined,
    gdriveTokenId: typeof data.gdriveTokenId === 'string' ? data.gdriveTokenId : '',
    sourceImageFileId: typeof data.sourceImageFileId === 'string' ? data.sourceImageFileId : undefined,
    sourceImageUrl: typeof data.sourceImageUrl === 'string' ? data.sourceImageUrl : undefined,
    sheetId: typeof data.sheetId === 'string' ? data.sheetId : '',
    outputFolderId: typeof data.outputFolderId === 'string' ? data.outputFolderId : '',
    creationTimestamp: typeof data.creationTimestamp === 'number' ? data.creationTimestamp : 0,
    systemPrompt: typeof data.systemPrompt === 'string' ? data.systemPrompt : undefined,
    edit: typeof data.edit === 'boolean' ? data.edit : undefined,
    outputQuality: (data.outputQuality as GenerationJobProject['outputQuality']) ?? undefined,
    outputShape: (data.outputShape as GenerationJobProject['outputShape']) ?? undefined,
  };
}

export default function useGetGenerationJobProject(id: string | undefined) {
  const [uid, setUid] = useState<string | null>(null);
  const [project, setProject] = useState<GenerationJobProject | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    const unsubAuth = firebaseAuth.onAuthStateChanged((user) => {
      setUid(user?.uid ?? null);
    });
    return () => {
      unsubAuth();
    };
  }, []);

  useEffect(() => {
    if (!uid || !id) {
      setProject(undefined);
      setLoading(false);
      return;
    }
    setLoading(true);
    const docRef = doc(firebaseFirestore, 'user', uid, env.collection.generationJobProject, id);
    const unsub: Unsubscribe = onSnapshot(
      docRef,
      (snap) => {
        setProject(snap.exists() ? docToProject(snap.id, snap.data()) : undefined);
        setLoading(false);
      },
      (err) => {
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      },
    );
    return () => unsub();
  }, [uid, id]);

  const refetch = async () => {
    if (!uid || !id) return;
    const docRef = doc(firebaseFirestore, 'user', uid, env.collection.generationJobProject, id);
    const snap = await getDoc(docRef);
    setProject(snap.exists() ? docToProject(snap.id, snap.data()) : undefined);
  };

  return {
    project,
    loading,
    error,
    refetch,
  };
}
